import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import { getMyInvitations } from '../../../services/invitation/invitationService'
import type { MyInvitation } from '../../../services/invitation/types'
import { selectPendingInvitations } from '../helper/pendingInvitations'

export interface UsePendingInvitationsResult {
  /** The invitations nobody has accepted, newest first. */
  pendingInvitations: MyInvitation[]
  /**
   * A fetch since the caller mounted succeeded and nothing is in flight. A
   * cached list can predate the last accept, and a failed fetch leaves that list
   * in place, so anything that acts on the list once (the popup) waits for this.
   */
  isFresh: boolean
}

/**
 * SN-7849 — the signed-in person's pending invitations (GET /invitations, the
 * list mobile Home reads). `staleTime` is 0 so Home reads it again every time
 * it mounts: an invitation accepted in another tab or in the app never lingers.
 */
export const usePendingInvitations = (
  userId?: string | null
): UsePendingInvitationsResult => {
  'use memo'

  const { data, isFetchedAfterMount, isFetching, isSuccess } = useQuery({
    queryKey: queryKeys.invitation.mine(userId),
    queryFn: getMyInvitations,
    enabled: Boolean(userId),
    staleTime: 0,
  })

  return {
    pendingInvitations: selectPendingInvitations(data),
    // A failed fetch also counts as fetched after mount; only success makes the
    // list fresh.
    isFresh: isFetchedAfterMount && isSuccess && !isFetching,
  }
}
